/**
 * What each Reference data column sorts by (f2.2).
 *
 * Three tables live on this screen and every one of them obeys the house
 * rule stated in `@/ui/tableSort`: every rendered column sorts, except the
 * column that holds the row actions. The rules are pure functions here so
 * they can be tested without rendering a table.
 *
 * A cell's sort value is not what the cell draws. Active renders a badge and
 * sorts by the flag behind it; the alias chips sort by their text, so a
 * country with no alias files last in both directions rather than first in
 * one of them.
 */

import type { Country, LookupValue, ResourceBuMapping } from '@/api/client';
import type { SortState, SortValue } from '@/ui/tableSort';

/* ---- A registered lookup type's values ---- */

export type LookupSortColumn = 'code' | 'name' | 'order' | 'active';

/** Registry order, which is the order the server already sent them in. */
export const LOOKUP_DEFAULT_SORT: SortState<LookupSortColumn> = {
  column: 'order',
  direction: 'asc',
};

export function lookupSortValue(value: LookupValue, column: LookupSortColumn): SortValue {
  switch (column) {
    case 'code':
      return value.code;
    case 'name':
      return value.name;
    case 'order':
      return value.sort_order;
    case 'active':
      return value.is_active;
  }
}

/* ---- Countries ---- */

export type CountrySortColumn = 'code' | 'name' | 'aliases' | 'active';

export const COUNTRY_DEFAULT_SORT: SortState<CountrySortColumn> = {
  column: 'code',
  direction: 'asc',
};

export function countrySortValue(country: Country, column: CountrySortColumn): SortValue {
  switch (column) {
    case 'code':
      return country.code;
    case 'name':
      return country.name;
    // The chips as one string: a country with no spelling sorts as empty,
    // and `compareValues` files empty last whichever way the column runs.
    case 'aliases':
      return country.aliases.join(' ');
    case 'active':
      return country.is_active;
  }
}

/* ---- The Resource BU mapping ---- */

export type MappingSortColumn =
  | 'rawValue'
  | 'canonicalPsu'
  | 'country'
  | 'location'
  | 'status';

export const MAPPING_DEFAULT_SORT: SortState<MappingSortColumn> = {
  column: 'rawValue',
  direction: 'asc',
};

export function mappingSortValue(
  mapping: ResourceBuMapping,
  column: MappingSortColumn,
): SortValue {
  switch (column) {
    case 'rawValue':
      return mapping.raw_value;
    case 'canonicalPsu':
      return mapping.canonical_psu;
    // The label the reader sees, not the code: the column is read as words.
    case 'country':
      return mapping.country?.name ?? null;
    case 'location':
      return mapping.location_name ?? null;
    case 'status':
      return mapping.status.name;
  }
}
